import React from "react";
import { XMarkIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import FileUpload from "../../FormComponents/FileUpload";

const ConvertToCustomerModal = ({
  show,
  onClose,
  loading,
  customerAdminFormData,
  setCustomerAdminFormData,
  employeeConfig,
  handleConvertToCustomer,
  errors,
  setErrors
}) => {
  if (!show) return null;

  const resetAndClose = () => {
    onClose();
    setCustomerAdminFormData({
      firstname: '',
      lastname: '',
      email: '',
      password: '',
      phone: '',
      customername: '',
      location: '',
      department: '',
      employeecount: '',
      price: '',
      logoImg: null
    });
    // clear any validation errors maintained by parent
    setErrors({});
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" 
          onClick={resetAndClose}
        ></div>

        <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <form onSubmit={handleConvertToCustomer}>
            <div className="px-6 pt-5 pb-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">Create Customer Administrator</h3>
                  <p className="text-sm opacity-80 mt-1">Add administrator user for {customerAdminFormData.customername}</p>
                </div>
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="hover:opacity-80"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              {/* Form Fields */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      First Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerAdminFormData.firstname}
                      onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, firstname: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="John"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={customerAdminFormData.lastname}
                      onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, lastname: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={customerAdminFormData.email}
                    onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, email: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    placeholder="admin@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={customerAdminFormData.phone}
                    onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, phone: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <FileUpload
                    value={customerAdminFormData.logoImg || ''}
                    onChange={(logoImg) => setCustomerAdminFormData({...customerAdminFormData, logoImg})}
                    label="Company Logo"
                    required={true}
                    helperText="Upload a logo image (JPG, PNG, SVG)"
                    allowedTypes={["image/jpeg", "image/png", "image/svg+xml"]}
                    error={errors.logoImg}
                  />
                  {errors.logoImg && <p className="text-red-500 text-xs mt-1">{errors.logoImg}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={customerAdminFormData.customername}
                    onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, customername: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-gray-50"
                    placeholder="Company name"
                    readOnly
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Location
                    </label>
                    <input
                      type="text"
                      value={customerAdminFormData.location}
                      onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, location: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="City, Country"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Department
                    </label>
                    <input
                      type="text"
                      value={customerAdminFormData.department}
                      onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, department: e.target.value})}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                      placeholder="e.g., IT, Security"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Employee Count
                    </label>
                    <select
                      value={customerAdminFormData.employeecount || ""}
                      onChange={(e) => {
                        const selectedRange = e.target.value;
                        const selectedConfig = employeeConfig.find(config => config.name === selectedRange);
                        setCustomerAdminFormData({
                          ...customerAdminFormData, 
                          employeecount: selectedRange,
                          price: selectedConfig ? selectedConfig.value : ''
                        });
                      }}
                      className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 bg-white dark:bg-gray-800"
                    >
                      <option value="">-- Select Employee Range --</option>
                      {employeeConfig.map((config) => (
                        <option key={config._id} value={config.name}>
                          {config.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                      Price
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        value={customerAdminFormData.price}
                        onChange={(e) => setCustomerAdminFormData({...customerAdminFormData, price: e.target.value})}
                        className="w-full pl-7 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                  <div className="flex items-start space-x-3">
                    <InformationCircleIcon className="w-5 h-5 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">What happens next?</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>Customer admin account will be created with the provided credentials</li>
                        <li>Lead status will be automatically updated to "Confirmed"</li>
                        <li>Customer will gain access to the platform</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  type="button"
                  onClick={resetAndClose}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-blue-600 dark:bg-green-500 text-white rounded-md hover:bg-green-700 dark:hover:bg-green-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed font-medium"
                >
                  {loading ? 'Creating...' : 'Convert & Create Admin'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConvertToCustomerModal;
